(() => {
  if (window.atlanticStoreSearch) return;
  const cache = new Map();
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const match = (product, term) => normalize(term).trim().split(/\s+/).filter(Boolean).every(word => normalize(product.text).includes(word));
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const readConfig = form => JSON.parse(form.querySelector('.atlantic-store-search-config').textContent);
  async function loadStore(handle, config, progress) {
    if (cache.has(handle)) return cache.get(handle);
    const promise = (async () => {
      async function page(number) {
        const url = new URL(config.root.replace(/\/?$/, '/') + 'collections/' + encodeURIComponent(handle), location.origin);
        url.searchParams.set('section_id', 'atlantic-store-index');
        url.searchParams.set('page', number);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        let response;
        for (let attempt = 0; attempt < 3; attempt++) {
          response = await fetch(url, {credentials:'same-origin'});
          if (response.ok) break;
          if (response.status !== 429 && response.status < 500) break;
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
        if (!response.ok) throw new Error('Store index unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-atlantic-store-index]');
        if (!node) throw new Error('Store index missing');
        return JSON.parse(node.textContent);
      }
      const first = await page(1);
      const products = new Map(first.products.map(p => [String(p.id), p]));
      let current = 2;
      if (progress) progress(products.size, first.total);
      async function worker() {
        while (current <= first.pages) {
          const number = current++;
          const result = await page(number);
          result.products.forEach(p => products.set(String(p.id), p));
          if (progress) progress(products.size, first.total);
        }
      }
      await Promise.all([worker(), worker(), worker()]);
      if (products.size !== first.total) throw new Error('Store changed during search; retry');
      return [...products.values()];
    })();
    cache.set(handle, promise);
    try { return await promise; } catch (error) { cache.delete(handle); throw error; }
  }
  function card(product) {
    return '<li><a class="atlantic-store-card" href="' + escape(product.url) + '">' +
      (product.image ? '<img src="' + escape(product.image) + '" alt="' + escape(product.title) + '" loading="lazy" width="480" height="480">' : '') +
      '<h3>' + escape(product.title) + '</h3><span>' + escape(product.price) + '</span></a></li>';
  }
  function setup() {
    document.querySelectorAll('form.search').forEach(form => {
      const storeInput = form.querySelector('.js-atlantic-parent-input');
      const selector = form.querySelector('.js-atlantic-parent-category');
      if (!storeInput || !selector || form.dataset.atlanticStoreReady) return;
      form.dataset.atlanticStoreReady = 'true';
      const config = readConfig(form);
      const input = form.querySelector('.js-search-input');
      const params = new URL(location.href).searchParams;
      const requested = params.get('atlantic_store') || 'all';
      const options = [...selector.querySelectorAll('[data-value]')];
      const valid = requested === 'all' || options.some(option => option.dataset.value === requested);
      const selected = valid ? requested : 'all';
      storeInput.value = selected;
      const selectedOption = options.find(option => option.dataset.value === selected);
      if (selectedOption) {
        selector.selectedOption = selectedOption;
        options.forEach(option => option.setAttribute('aria-selected', String(option === selectedOption)));
        const label = selector.querySelector('.custom-select__btn > span');
        if (label) label.textContent = selectedOption.textContent.trim();
      }
      form.atlanticBuildQuery = term => term;
      let sequence = 0;
      const predictive = form.closest('predictive-search');
      if (predictive && typeof predictive.getResults === 'function') {
        const original = predictive.getResults.bind(predictive);
        predictive.getResults = async term => {
          const request = ++sequence;
          const handle = storeInput.value;
          if (handle === 'all') return original(term);
          predictive.latestQueryKey = 'store:' + request;
          predictive.cachedResults = {};
          predictive.results.innerHTML = '<p class="predictive-search__no-results">' + escape(config.loading) + '</p>';
          predictive.open();
          try {
            const products = await loadStore(handle, config);
            if (request !== sequence || storeInput.value !== handle || input.value.trim() !== term) return;
            const matches = products.filter(p => match(p, term));
            predictive.results.innerHTML = '<ul class="predictive-search__list" role="list">' +
              matches.slice(0,8).map(p => '<li class="predictive-search__item" role="option"><a class="predictive-result js-search-link" href="' + escape(p.url) + '">' + escape(p.title) + '</a></li>').join('') +
              '<li class="predictive-search__item js-submit"><button type="submit" class="predictive-search__view-all js-search-link">' + escape(config.results) + ' (' + matches.length + ')</button></li></ul>';
            predictive.open();
          } catch (error) {
            if (request === sequence) predictive.results.innerHTML = '<p class="predictive-search__no-results">' + escape(config.failed) + '</p>';
          }
        };
      }
      selector.addEventListener('change', event => {
        const value = event.detail?.selectedValue;
        if (!value) return;
        storeInput.value = value;
        ++sequence;
        if (predictive) {
          predictive.latestQueryKey = '';
          predictive.removeAttribute('results');
          predictive.close();
          if (input.value.trim()) predictive.getResults(input.value.trim());
        }
      });
      form.addEventListener('submit', () => {
        form.querySelector('input[name="type"]').value = 'product';
      });
      if (input.id === 'main-search' && selected !== 'all') {
        const section = form.closest('.shopify-section');
        const native = section.querySelector('[data-atlantic-native-results]');
        const host = section.querySelector('[data-atlantic-store-results]');
        if (!host || !native) return;
        native.hidden = true;
        host.hidden = false;
        host.textContent = config.loading;
        const term = params.get('q') || '';
        const storeName = selectedOption ? selectedOption.textContent.trim() : selected;
        loadStore(selected, config, (count,total) => {
          host.textContent = config.loading + ' ' + count + ' / ' + total;
        }).then(products => {
          let matches = products.filter(p => match(p, term));
          document.title = storeName + ' · ' + term + ' · ' + matches.length + ' ' + config.results + ' – Atlantic Shopping Mall';
          let current = 1;
          const size = 16;
          function render() {
            const pages = Math.max(1, Math.ceil(matches.length / size));
            host.innerHTML = '<p role="status">' + escape(storeName) + ' · ' + escape(term) + ' · ' + matches.length + ' ' + escape(config.results) + '</p>' +
              '<ul class="atlantic-store-grid" role="list">' + matches.slice((current - 1) * size, current * size).map(card).join('') + '</ul>' +
              '<nav class="atlantic-store-pages" aria-label="Pagination"><button data-previous ' + (current === 1 ? 'disabled' : '') + '>' + escape(config.previous) + '</button><span>' + current + ' / ' + pages + '</span><button data-next ' + (current === pages ? 'disabled' : '') + '>' + escape(config.next) + '</button></nav>';
            host.querySelector('[data-previous]').onclick = () => { current--; render(); host.scrollIntoView({block:'start'}); };
            host.querySelector('[data-next]').onclick = () => { current++; render(); host.scrollIntoView({block:'start'}); };
          }
          render();
        }).catch(() => { host.textContent = config.failed; });
      }
    });
  }
  window.atlanticStoreSearch = {match, loadStore};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup); else setup();
})();
